/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRef } from 'react';
import type { Route } from './+types/_app';
import { usePageUIConfig, mainPaddingDataAttributes } from '@/lib/routes/page-ui-config';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { type ShopperProducts } from '@/scapi';
import { getLogger } from '@/lib/logger.server';
import { WishlistMergeToast } from '@/components/wishlist/wishlist-merge-toast';
import { useAuth } from '@/providers/auth';
import { useWishlistSession } from '@/providers/wishlist';
import { SkipLink } from '@/components/skip-link';
/** @sfdc-extension-block-start SNK */
import { Outlet, type ShouldRevalidateFunctionArgs } from 'react-router';
import { getActionPath, isIdentityMutation } from '@/lib/revalidation/routes/shared';
import { GLOBAL_CONTENT_SLOT_PAGE_ID } from '@/extensions/snk/lib/content-slot-page-ids';
import type { ContentSlotPageWithComponentData } from '@/extensions/snk/lib/content-slot-page';
import { fetchContentSlotPageWithComponentData } from '@/extensions/snk/lib/contentSlotLoader.server';
import { GlobalHeader } from '@/features/global-header';
import { GlobalFooter } from '@/features/global-footer';
import { GLOBAL_FOOTER_COMPONENT_ID } from '@/features/global-footer/constants';
import {
    fetchComponentWithComponentData,
    type ComponentWithComponentData,
} from '@/lib/page-designer/component-loader.server';
import { getClientRequestInfo } from '@/lib/client-request-info.server';
import { getWalletProviderForUserAgent, type WalletProvider } from '@/extensions/snk/lib/device-utils';
import { getSlugMap } from '@/extensions/snk/middlewares/slug-map.server';
/** @sfdc-extension-block-end SNK */

type LoaderData = {
    root: Promise<ShopperProducts.schemas['Category']>;
    /** @sfdc-extension-block-start SNK */
    globalContentSlots: Promise<ContentSlotPageWithComponentData>;
    globalFooter: Promise<ComponentWithComponentData | null>;
    mobileAppStorePlatform: WalletProvider;
    /** @sfdc-extension-block-end SNK */
};

/**
 * We're using the `shouldRevalidate` functionality to only load the navigation menu categories from the server on the
 * very first navigation to a page using the (default) `_app` layout. Depth 1 fetches the root category with its
 * immediate children for the nav shell.
 *
 * **Note:** If there is a desire/intent to customize that behavior, the `shouldRevalidate` function can be modified
 * accordingly. For example, a client-side storage for the categories could be introduced to implement any desired
 * caching strategy, including specific reload/refresh intervals/conditions.
 * @see {@link https://reactrouter.com/start/framework/route-module#shouldrevalidate}
 */
/** @sfdc-extension-block-start SNK
 * The shell binds wishlist identity via `useWishlistSession` (upstream v2026.8 module store). This loader must
 * re-run on a session-identity change or the header heart stays stuck on the guest state for the tab's life
 * (SNKSN-152 review). Two triggers: (1) identity mutations — login/signup/logout form/fetcher submissions incl. the
 * post-redirect navigation; (2) an in-place `revalidator.revalidate()` (no form, same URL), which is how the
 * ProfileDrawer login re-seeds without a redirect. Plain navigations change the URL and stay suppressed — the pinned
 * categories/content slots (refs below) don't change per session.
 */
export function shouldRevalidate({
    currentUrl,
    nextUrl,
    formMethod,
    formAction,
}: ShouldRevalidateFunctionArgs): boolean {
    if (formMethod && formMethod !== 'GET') {
        const actionPath = getActionPath(formAction, currentUrl.origin);
        return Boolean(actionPath && isIdentityMutation(actionPath));
    }
    return !formMethod && currentUrl.href === nextUrl.href;
}
/** @sfdc-extension-block-end SNK */

export function loader(loaderArgs: Route.LoaderArgs): LoaderData {
    const { context } = loaderArgs;
    const logger = getLogger(context);
    const config = getConfig(context);
    const { rootCategoryId, maxDepth } = config.pages.navigation;

    logger.debug('AppLayout: loader starting', { rootCategoryId, maxDepth });

    /** @sfdc-extension-block-start SNK */
    // c_slugToIdMap is server-only (hierarchy-url.server.ts). Strip it so the full map is not streamed to the client.
    const rootCategoryPromise = getSlugMap(context).then(({ rootCategory }) => {
        // oxlint-disable-next-line @typescript-eslint/no-unused-vars -- omit c_slugToIdMap from client payload
        const { c_slugToIdMap, ...navRoot } = rootCategory;
        return navRoot;
    });
    /** @sfdc-extension-block-end SNK */

    return {
        /** @sfdc-extension-block-start SNK */
        root: rootCategoryPromise,
        globalContentSlots: fetchContentSlotPageWithComponentData(loaderArgs, {
            pageId: GLOBAL_CONTENT_SLOT_PAGE_ID,
        }),
        globalFooter: fetchComponentWithComponentData(loaderArgs, {
            componentId: GLOBAL_FOOTER_COMPONENT_ID,
        }),
        mobileAppStorePlatform:
            getWalletProviderForUserAgent(getClientRequestInfo(loaderArgs.request).clientUserAgent ?? '') ?? 'google',
        /** @sfdc-extension-block-end SNK */
    };
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure:
 * - Header with navigation
 * - Main content area (via `<Outlet/>`)
 * - Footer
 *
 * Routes that need this layout should be prefixed with `_app.` in their filename.
 * For routes without default header/footer (e.g., login), use the `_empty.` prefix instead.
 */
export default function DefaultLayout({
    loaderData: {
        root,
        /** @sfdc-extension-block-start SNK */
        globalContentSlots,
        globalFooter,
        mobileAppStorePlatform,
        /** @sfdc-extension-block-end SNK */
    },
}: {
    loaderData: LoaderData;
}) {
    const refRoot = useRef<Promise<ShopperProducts.schemas['Category']> | undefined>(undefined);
    /** @sfdc-extension-block-start SNK
     * Categories/global PD content ARE pinned — session-invariant, so pinning avoids a re-suspend on identity
     * revalidation (see `shouldRevalidate`).
     */
    const refGlobalContentSlots = useRef<Promise<ContentSlotPageWithComponentData> | undefined>(undefined);
    if (!refGlobalContentSlots.current) {
        refGlobalContentSlots.current = globalContentSlots;
    }
    const refGlobalFooter = useRef<Promise<ComponentWithComponentData | null> | undefined>(undefined);
    if (!refGlobalFooter.current) {
        refGlobalFooter.current = globalFooter;
    }
    /** @sfdc-extension-block-end SNK */
    if (!refRoot.current) {
        refRoot.current = root;
    }

    const mainPaddingAttrs = mainPaddingDataAttributes(usePageUIConfig());

    /** @sfdc-extension-line SNK — bind module-level wishlist store to current shopper (upstream v2026.8) */
    useWishlistSession(useAuth()?.customerId ?? null);

    return (
        <>
            <SkipLink />
            <WishlistMergeToast />
            {/* @sfdc-extension-block-start SNK */}
            <GlobalHeader rootCategory={refRoot.current} globalContentSlots={refGlobalContentSlots.current} />
            {/* @sfdc-extension-block-end SNK */}
            <main id="main-content" tabIndex={-1} className="grow pt-8" {...mainPaddingAttrs}>
                <Outlet />
            </main>
            {/* @sfdc-extension-block-start SNK */}
            <GlobalFooter
                globalFooter={refGlobalFooter.current ?? globalFooter}
                mobileAppStorePlatform={mobileAppStorePlatform}
            />
            {/* @sfdc-extension-block-end SNK */}
        </>
    );
}
